if (!customElements.get('basz-shade-card')) {
  customElements.define('basz-shade-card', class extends HTMLElement {
    connectedCallback() {
      this.setupFrame = requestAnimationFrame(() => this.initialize());
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.setupFrame);
      this.listeners?.abort();
      this.requestNumber = (this.requestNumber || 0) + 1;
    }

    initialize() {
      this.image = this.querySelector('[data-basz-card-primary]');
      const data = this.querySelector('[data-basz-card-variants]');
      if (!this.image || !data) return;
      let config;
      try { config = JSON.parse(data.textContent); } catch { return; }
      this.variants = config.variants || [];
      this.buttons = Array.from(this.querySelectorAll('[data-basz-variant]'));
      this.links = Array.from(this.querySelectorAll('.basz-card-product-link, .card__heading a'));
      this.listeners?.abort();
      this.listeners = new AbortController();
      const listenerOptions = { signal: this.listeners.signal };
      this.requestNumber = 0;
      this.imageCache = new Map();
      const primaryVariant = this.variants.find((variant) => variant.mediaId === config.primaryMediaId);
      this.primary = {
        id: primaryVariant?.id,
        mediaId: config.primaryMediaId,
        src: this.image.getAttribute('src'),
        srcset: this.image.getAttribute('srcset'),
        alt: this.image.alt,
        url: primaryVariant?.url,
        shopLabel: primaryVariant?.shopLabel,
      };
      this.committed = this.primary;
      this.current = this.primary;
      this.initialLinks = this.links.map((link) => ({ link, href: link.getAttribute('href'), label: link.getAttribute('aria-label') }));
      this.updateLinks(this.primary);
      this.updateSelection(this.primary.id);

      this.buttons.forEach((button) => {
        const variant = this.variants.find((item) => String(item.id) === button.dataset.baszVariant);
        if (!variant) return;
        button.disabled = false;
        button.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'mouse') this.show(variant);
        }, listenerOptions);
        button.addEventListener('focus', () => this.show(variant), listenerOptions);
        button.addEventListener('click', () => {
          this.committed = variant;
          this.show(variant);
        }, listenerOptions);
      });

      this.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse' || this.dataset.previewHover !== 'true') return;
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
        if (this.contains(document.activeElement)) return;
        const alternate = this.variants.find((variant) => variant.mediaId !== this.committed.mediaId);
        if (alternate) this.show(alternate);
      }, listenerOptions);
      this.addEventListener('pointerleave', () => {
        if (!this.contains(document.activeElement)) this.show(this.committed);
      }, listenerOptions);
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.show(this.committed);
      }, listenerOptions);
    }

    loadImage(state) {
      const key = `${state.src}|${state.srcset || ''}`;
      if (!this.imageCache.has(key)) {
        const promise = new Promise((resolve) => {
          const pending = new Image();
          pending.onload = async () => {
            try { await pending.decode(); } catch {}
            resolve(pending.naturalWidth > 0);
          };
          pending.onerror = () => resolve(false);
          pending.sizes = this.image.sizes;
          if (state.srcset) pending.srcset = state.srcset;
          pending.src = state.src;
        });
        this.imageCache.set(key, promise);
      }
      return this.imageCache.get(key);
    }

    async show(state) {
      if (!state?.src) return;
      const request = ++this.requestNumber;
      if (!(await this.loadImage(state)) || !this.isConnected || request !== this.requestNumber) return;
      if (state.srcset) this.image.setAttribute('srcset', state.srcset);
      else this.image.removeAttribute('srcset');
      this.image.src = state.src;
      this.image.alt = state.alt;
      this.current = state;
      this.updateLinks(state);
      this.updateSelection(state.id);
    }

    updateLinks(state) {
      this.initialLinks.forEach(({ link, href, label }) => {
        link.setAttribute('href', state.url || href);
        if (link.classList.contains('basz-card-product-link')) {
          if (state.shopLabel || label) link.setAttribute('aria-label', state.shopLabel || label);
        }
      });
    }

    updateSelection(variantId) {
      this.buttons.forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.baszVariant === String(variantId)));
      });
    }
  });
}
